import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { isValidatePassword } from '@utils/regex';
import { useQueryClient } from 'react-query';
import { Toast } from '@components/_atoms/Toast';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

// constants
import { ButtonColor, ButtonType, TextInputVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { usePatchUserPasswordFetch } from '@hooks/fetch/useUserFetch';
import { EditPasswordFormType } from '../types';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form`
  display: flex;
  flex-direction: column;
  gap: 80px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export default function PasswordEditForm() {
  const { t } = useTranslation('user');
  const router = useRouter();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<EditPasswordFormType>({
    password: '',
    passwordConfirm: '',
  });
  const [errorMessage, setErrorMessage] = useState<EditPasswordFormType>({
    password: '',
    passwordConfirm: '',
  });

  const disabled =
    !form.password ||
    !!errorMessage.password ||
    !form.passwordConfirm ||
    !!errorMessage.passwordConfirm ||
    form.password !== form.passwordConfirm;

  const { patchUserPasswordFetchMutate } = usePatchUserPasswordFetch();

  // form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    checkValidation(name, value);
  };

  // check validation
  const checkValidation = (name: string, value: string) => {
    let message = '';
    if (name === 'password' && !isValidatePassword(value)) {
      message = t('edit.password-error-message');
    }
    if (name === 'passwordConfirm' && value !== form.password) {
      message = t('edit.passwordConfirm-error-message');
    }

    setErrorMessage((prev) => ({
      ...prev,
      [name]: message,
    }));
  };

  // 비밀번호 변경 submit
  const handleFormSubmit = () => {
    patchUserPasswordFetchMutate(
      { password: form.password },
      {
        onSuccess: () => {
          router.back();
          Toast.success(t('edit.success-message'));
          queryClient.invalidateQueries(['user']);
        },
      },
    );
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <FormWrapper>
        <TextInputBox
          title={t('edit.password-title')}
          subTitle={t('edit.password-subTitle')}
          textInput={{
            variant: errorMessage.password ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
            type: 'password',
            name: 'password',
            value: form.password,
            onChange: handleFormChange,
            placeholder: t('edit.password-placeholder'),
          }}
          errorMessage={errorMessage.password}
          isRequired
        />
        <TextInputBox
          title={t('edit.passwordConfirm-title')}
          textInput={{
            variant: errorMessage.passwordConfirm ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
            type: 'password',
            name: 'passwordConfirm',
            value: form.passwordConfirm,
            onChange: handleFormChange,
            placeholder: t('edit.passwordConfirm-placeholder'),
          }}
          errorMessage={errorMessage.passwordConfirm}
          isRequired
        />
      </FormWrapper>

      <DefaultButtonBox
        button1={
          <Button
            text={t('edit.back-button')}
            type={ButtonType.Button}
            color={ButtonColor.OutlinedBlack}
            onClick={() => router.back()}
            steelbosoId={ButtonCode.BTN_BACK}
          />
        }
        button2={<Button disabled={disabled} text={t('edit.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />}
      />
    </Layout>
  );
}
